import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

// Helper to format ISO date string
const formatEventDate = (isoString) => {
  if (!isoString) return "—";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return String(isoString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return String(isoString);
  }
};

export default function InstituteEventDetailsModal({ open, onClose, institute }) {
  const [activeTab, setActiveTab] = useState(0); // 0: FDP, 1: Tech Camp
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [error, setError] = useState(null);

  // Fetch event details whenever modal opens for a given institute
  useEffect(() => {
    if (!open || !institute?.institute_id) {
      setEventDetails(null);
      setError(null);
      setSearchQuery("");
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let res;
        try {
          res = await api.get(`/trainer_assign/institute-events/${institute.institute_id}`);
        } catch (err) {
          if (err?.response?.status === 404) {
            res = await api.get(`/institute-events/${institute.institute_id}`);
          } else {
            throw err;
          }
        }
        setEventDetails(res?.data || null);
      } catch (err) {
        console.error("Error fetching institute event details:", err);
        setError("Failed to fetch FDP & Tech Camp details for this institute.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [open, institute?.institute_id]);

  // Extract current tab data
  const fdpSection = eventDetails?.FDP || eventDetails?.fdp || {};
  const tcSection =
    eventDetails?.Tech_camp ||
    eventDetails?.Tech_Camp ||
    eventDetails?.tech_camp ||
    eventDetails?.["tech camp"] ||
    {};

  const isFdp = activeTab === 0;
  const currentSection = isFdp ? fdpSection : tcSection;

  const totalLimit = currentSection?.total ?? 0;
  const usedLimit = currentSection?.used ?? 0;
  const availableLimit = Math.max(0, totalLimit - usedLimit);
  const dataList = Array.isArray(currentSection?.data) ? currentSection.data : [];

  // Filter list based on search query
  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return dataList;
    const q = searchQuery.toLowerCase().trim();
    return dataList.filter((item) => {
      const domain = String(item.domain_name || "").toLowerCase();
      const type = String(item.type || item.Type || item.slot_type || "").toLowerCase();
      const start = String(item.start_date || "").toLowerCase();
      const end = String(item.end_date || "").toLowerCase();
      return (
        domain.includes(q) ||
        type.includes(q) ||
        start.includes(q) ||
        end.includes(q)
      );
    });
  }, [dataList, searchQuery]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "12px", sm: "16px" },
          overflow: "hidden",
          p: { xs: 1, sm: 2 },
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 1,
          pt: 1,
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1E293B",
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: "18px", sm: "20px" },
              lineHeight: 1.3,
            }}
          >
            {institute?.institute_name || "Institute Events & Limits"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#64748B",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "13px",
              mt: 0.3,
            }}
          >
            {isFdp
              ? "FDP Participation & Results History"
              : "Tech Camp Participation & Results History"}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#64748B",
            "&:hover": { bgcolor: "#F1F5F9", color: "#1E293B" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ px: { xs: 1, sm: 2 }, borderBottom: "1px solid #E2E8F0" }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            setSearchQuery("");
          }}
          sx={{
            minHeight: "44px",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              fontFamily: "'Poppins', sans-serif",
              minHeight: "44px",
              py: 1,
              px: 2.5,
              color: "#64748B",
              "&.Mui-selected": {
                color: "#2563EB",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#2563EB",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab label="FDP" />
          <Tab label="Tech Camp" />
        </Tabs>
      </Box>

      {/* Dialog Body Content */}
      <DialogContent sx={{ px: { xs: 1, sm: 2 }, py: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            {error}
          </Alert>
        )}

        {/* Limit Section Heading */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "11px",
            fontWeight: 700,
            color: "#64748B",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontFamily: "'Poppins', sans-serif",
            mb: 1.5,
          }}
        >
          {isFdp ? "EDUCATOR LIMIT" : "TECH CAMP LIMIT"}
        </Typography>

        {/* 3 Metric Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Card 1: Total Limit */}
          <Box
            sx={{
              border: "1px solid #DBEAFE",
              bgcolor: "#F8FAFC",
              borderRadius: "12px",
              py: 2.2,
              px: 2,
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#2563EB",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              TOTAL LIMIT
            </Typography>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#1E3A8A",
                mt: 0.5,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {isLoading ? <Skeleton width={50} sx={{ mx: "auto" }} /> : totalLimit}
            </Typography>
          </Box>

          {/* Card 2: Used */}
          <Box
            sx={{
              border: "1px solid #FEF3C7",
              bgcolor: "#FFFDF5",
              borderRadius: "12px",
              py: 2.2,
              px: 2,
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#D97706",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              USED
            </Typography>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#B45309",
                mt: 0.5,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {isLoading ? <Skeleton width={50} sx={{ mx: "auto" }} /> : usedLimit}
            </Typography>
          </Box>

          {/* Card 3: Available */}
          <Box
            sx={{
              border: "1px solid #D1FAE5",
              bgcolor: "#F0FDF4",
              borderRadius: "12px",
              py: 2.2,
              px: 2,
              textAlign: "center",
              transition: "all 0.2s ease",
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#059669",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              AVAILABLE
            </Typography>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#047857",
                mt: 0.5,
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {isLoading ? <Skeleton width={50} sx={{ mx: "auto" }} /> : availableLimit}
            </Typography>
          </Box>
        </Box>

        {/* Search details input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            placeholder="Search details..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94A3B8", fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
                fontFamily: "'Poppins', sans-serif",
                height: 38,
                bgcolor: "#FFFFFF",
              },
            }}
          />
        </Box>

        {/* Table matching the provided image */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            overflowX: "auto",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table size="small" sx={{ minWidth: 600 }} aria-label="institute events table">
            <TableHead>
              <TableRow sx={{ bgcolor: "#2563EB" }}>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  DOMAIN NAME
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  PARTICIPATE
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  PASSED
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  TYPE
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  START DATE
                </TableCell>
                <TableCell
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "11.5px",
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderBottom: "none",
                  }}
                >
                  END DATE
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                Array.from(new Array(3)).map((_, idx) => (
                  <TableRow key={`skeleton-row-${idx}`}>
                    <TableCell><Skeleton width="75%" height={20} /></TableCell>
                    <TableCell><Skeleton width={35} height={20} /></TableCell>
                    <TableCell><Skeleton width={35} height={20} /></TableCell>
                    <TableCell><Skeleton width={50} height={20} /></TableCell>
                    <TableCell><Skeleton width={80} height={20} /></TableCell>
                    <TableCell><Skeleton width={80} height={20} /></TableCell>
                  </TableRow>
                ))
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{
                        color: "#EF4444",
                        fontWeight: 600,
                        fontSize: "13.5px",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {isFdp
                        ? "No Fdp Found for this Institute"
                        : "No Tech Camp Found for this Institute"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList.map((row, idx) => (
                  <TableRow
                    key={row.bookslot_id || idx}
                    sx={{
                      "&:hover": { bgcolor: "#F8FAFC" },
                      borderBottom: "1px solid #F1F5F9",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#1E293B",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {row.domain_name || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#475569",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {row.participated ?? 0}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#475569",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {row.passed ?? 0}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#475569",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {row.type || row.Type || row.slot_type || "Complementary"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#475569",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {formatEventDate(row.start_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#475569",
                        fontFamily: "'Poppins', sans-serif",
                        py: 1.5,
                      }}
                    >
                      {formatEventDate(row.end_date)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            bgcolor: "#F1F5F9",
            color: "#334155",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            fontFamily: "'Poppins', sans-serif",
            borderRadius: "8px",
            px: 3,
            py: 0.8,
            boxShadow: "none",
            "&:hover": { bgcolor: "#E2E8F0" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
